import React, { useEffect, useState } from "react";
import axios from "axios";
import {
 Box,
 TextField,
 Typography,
 Avatar,
 Divider,
 IconButton,
 Card,
 CardContent,
 MenuItem,
 Select,
 InputLabel,
 FormControl,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const OrderManagement = () => {
 const [orders, setOrders] = useState([]);
 const [orderIdFilter, setOrderIdFilter] = useState("");
 const [userNameFilter, setUserNameFilter] = useState("");
 const [statusFilter, setStatusFilter] = useState("");
 const [startDate, setStartDate] = useState("");
 const [endDate, setEndDate] = useState("");
 const [pincodeFilter, setPincodeFilter] = useState("");

 useEffect(() => {
  const fetchOrders = async () => {
   try {
    const response = await axios.get(
     "https://app-api.carexpert.org.in/api/orders",
    );
    setOrders(response.data);
   } catch (error) {
    console.error("Error fetching orders:", error);
   }
  };

  fetchOrders();
 }, []);

 const rows = orders.map((order, index) => ({
  id: order._id || index, // Safer unique id
  order,
  sNo: index + 1,
 }));

 const filteredRows = rows.filter((row) => {
  const matchesOrderId = orderIdFilter
   ? row.order.orderId?.toLowerCase().includes(orderIdFilter.toLowerCase())
   : true;
  const matchesMobile = userNameFilter
   ? row.order.user?.mobile?.toString().includes(userNameFilter)
   : true;
  const matchesStatus = statusFilter
   ? row.order.orderStatus?.toLowerCase().includes(statusFilter.toLowerCase())
   : true;

  const matchesPincode = pincodeFilter
   ? row.order.address?.pincode?.toString().includes(pincodeFilter)
   : true;

  const orderDate = new Date(row.order.createdAt);
  const matchesDateRange =
   (!startDate || orderDate >= new Date(startDate)) &&
   (!endDate || orderDate <= new Date(endDate));

  return (
   matchesOrderId &&
   matchesMobile &&
   matchesStatus &&
   matchesPincode &&
   matchesDateRange
  );
 });

 const UserProfileCard = ({ order }) => {
  const { user, car, address } = order;

  return (
   <Card
    sx={{
     display: "flex",
     flexDirection: "row",
     borderRadius: 3,
     boxShadow: 3,
     overflow: "hidden",
     width: "100%",
     height: "100%",
    }}
   >
    {/* Left Side - Avatar & Car */}
    <Box
     sx={{
      width: 150,
      bgcolor: "#000D6B",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      p: 1,
      background: "#000D6B",
     }}
    >
     <Avatar
      src={car?.images}
      sx={{
       width: 80,
       height: 80,
       mb: 1,
       bgcolor: "white",
       img: {
        objectFit: "contain",
        width: "100%",
        height: "100%",
       },
      }}
     />

     <Typography
      variant="caption"
      color="white"
      sx={{ textAlign: "center", fontWeight: 500 }}
     >
      {car?.title}
     </Typography>
    </Box>

    {/* Right Side - Info */}
    <CardContent sx={{ flex: 1, px: 2 }}>
     {/* User Info */}
     {user && (
      <>
       <Typography variant="subtitle2" fontWeight={600}>
        {user.name}
       </Typography>
       <Typography variant="body2" color="text.secondary">
        Mobile: {user.mobile}
       </Typography>
      </>
     )}

     {/* Address Info */}
     {address && (
      <>
       <Typography variant="body2">{address.houseFlatNumber}</Typography>
       <Typography variant="body2" color="text.secondary">
        {address.city}, {address.state} - {address.pincode}
       </Typography>
       <Typography variant="body2" color="text.secondary">
        {address.completeAddress}
       </Typography>
       <Typography variant="body2" color="text.secondary">
        Phone: {address.phoneNumber}
       </Typography>
      </>
     )}
    </CardContent>
   </Card>
  );
 };

 const columns = [
  { field: "sNo", headerAlign: "center", headerName: "S.No", width: 50 },
  {
   field: "userAndAddressInfo",
   headerAlign: "center",
   headerName: "User, Car & Address Info",
   width: 450,
   renderCell: (params) => {
    return <UserProfileCard order={params.row.order} />;
   },
  },

  {
   field: "orderAndCartInfo",
   headerName: "Order & Cart Info",
   width: 350,
   headerAlign: "center",
   renderCell: (params) => {
    const order = params.row.order;
    const cartItems = order.cart?.items || [];
    const slot = order.slot || [];

    return (
     <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Order Info */}
      <Typography variant="subtitle2" fontWeight={600}>
       Order Info
      </Typography>
      <Box sx={{ mb: 2 }}>
       <Typography variant="body2">OrderID: {order.orderId}</Typography>
       <Typography variant="body2">
        Razorpay: {order.razorpayOrderId}
       </Typography>
       <Typography variant="body2">
        Unique Order Id: {order.uniqueOrderId}
       </Typography>
       <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Slot Info
       </Typography>

       <Box sx={{ display: "flex", gap: 1.5 }}>
        <Typography variant="body2">
         <strong>Time:</strong> {slot?.time}
        </Typography>
        <Typography variant="body2">
         <strong>Date:</strong> {new Date(slot?.date).toLocaleDateString()}
        </Typography>
       </Box>
      </Box>

      {/* Cart Info */}
      <Typography variant="subtitle2" fontWeight={600}>
       Cart Items
      </Typography>
      <Box>
       {cartItems.length > 0 ? (
        cartItems.map((item, idx) => (
         <Box
          key={idx}
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
         >
          {item.product ? (
           // Render Product Item
           <>
            <Avatar src={item.product?.image} sx={{ width: 30, height: 30 }} />
            <Typography variant="body2">
             {item.product?.name} (₹{item.totalPrice})
            </Typography>
           </>
          ) : item.sparePart ? (
           // Render SparePart Item
           <>
            <Avatar
             src={item.sparePart?.image}
             sx={{ width: 30, height: 30 }}
            />
            <Typography variant="body2">
             {item.sparePart?.spareName} (₹{item.totalPrice})
            </Typography>
           </>
          ) : (
           <Typography variant="body2" color="error">
            Item type unknown or not available
           </Typography>
          )}
         </Box>
        ))
       ) : (
        <Typography variant="body2">No Items</Typography>
       )}
      </Box>
     </Box>
    );
   },
  },

  {
   field: "cartAndStatusSummary",
   headerName: "Cart & Status Info",
   headerAlign: "center",
   width: 500,
   renderCell: (params) => {
    const order = params.row.order;
    const cart = order.cart;
    const pricingDetails = order.pricingDetails;

    // Filter out pricingDetails fields with value "0"
    const filteredPricingDetails = Object.entries(pricingDetails || {}).filter(
     ([_, value]) => value !== "0",
    );

    return (
     <Box
      sx={{
       display: "flex",
       flexDirection: "row",
       gap: 2, // Space between the two sections
       width: "100%",
       p: 2,
       bgcolor: "#f9f9f9",
       borderRadius: 2,
      }}
     >
      {/* Cart Summary */}
      <Box
       sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1, // Take up equal space
        gap: 1,
        bgcolor: "#f1f1f1",
        p: 2,
        borderRadius: 1,
       }}
      >
       <Typography variant="subtitle2" sx={{ mb: 1 }}>
        🛒 Cart Summary
       </Typography>
       {cart ? (
        <>
         <Typography variant="body2">Items: {cart.totalItems}</Typography>
         <Typography variant="body2">Total: ₹{cart.totalPrice}</Typography>

         {filteredPricingDetails.map(([key, value]) => (
          <Typography key={key} variant="body2">
           {key
            .replace(/carexpert/g, "")
            .replace(/([A-Z])/g, " $1")
            .trim()}
           : ₹{value}
          </Typography>
         ))}
        </>
       ) : (
        <Typography variant="body2">No Cart Info</Typography>
       )}
      </Box>

      {/* Order Status */}
      <Box
       sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1, // Take up equal space
        gap: 1,
        bgcolor: "#e9e9e9",
        p: 2,
        borderRadius: 1,
       }}
      >
       <Typography variant="subtitle2" sx={{ mb: 1 }}>
        📦 Order Status
       </Typography>
       <Typography variant="body2">
        Order: {order.orderStatus || "N/A"}
       </Typography>
       <Typography variant="body2">
        Payment: {order.paymentStatus || "N/A"}
       </Typography>
       <Typography variant="body2">
        Payment mode: {order?.paymentMethod || "N/A"}
       </Typography>
       <Typography variant="body2">
        Placed on: {new Date(order.createdAt).toLocaleString()}
       </Typography>
      </Box>
     </Box>
    );
   },
  },
 ];

 return (
  <Box sx={{ width: "100%", p: 2 }}>
   {/* Filters */}
   <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
    <TextField
     label="Order ID"
     size="small"
     value={orderIdFilter}
     onChange={(e) => setOrderIdFilter(e.target.value)}
    />
    <TextField
     label="Mobile"
     size="small"
     value={userNameFilter}
     onChange={(e) => setUserNameFilter(e.target.value)}
    />
    <FormControl size="small" sx={{ minWidth: 150 }}>
     <InputLabel>Order Status</InputLabel>
     <Select
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      label="Order Status"
     >
      <MenuItem value="">All</MenuItem>
      <MenuItem value="confirmed">Confirmed</MenuItem>
      <MenuItem value="failed">Failed</MenuItem>
      <MenuItem value="pending">Pending</MenuItem>
     </Select>
    </FormControl>

    <TextField
     label="Pincode"
     size="small"
     value={pincodeFilter}
     onChange={(e) => setPincodeFilter(e.target.value)}
    />

    <TextField
     label="Start Date"
     type="date"
     size="small"
     InputLabelProps={{
      shrink: true,
     }}
     value={startDate}
     onChange={(e) => setStartDate(e.target.value)}
    />
    <TextField
     label="End Date"
     type="date"
     size="small"
     InputLabelProps={{
      shrink: true,
     }}
     value={endDate}
     onChange={(e) => setEndDate(e.target.value)}
    />
   </Box>

   {/* DataGrid */}
   <DataGrid
    rows={filteredRows}
    columns={columns}
    pageSize={10}
    rowsPerPageOptions={[5, 10, 20]}
    pagination
    disableSelectionOnClick
    autoHeight
    getRowHeight={() => "auto"} // Adjust row height for better visibility
    components={{
     Row: (props) => (
      <>
       <Divider sx={{ mb: 1 }} /> {/* Adding Divider Between Rows */}
       <Box {...props} />
      </>
     ),
    }}
   />
  </Box>
 );
};

export default OrderManagement;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import {
//  Box,
//  TextField,
//  Typography,
//  Avatar,
//  Divider,
//  IconButton,
//  Card,
//  CardContent,
// } from "@mui/material";
// import { DataGrid } from "@mui/x-data-grid";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// import ExpandLessIcon from "@mui/icons-material/ExpandLess";
// const OrderManagement = () => {
//  const [orders, setOrders] = useState([]);
//  const [orderIdFilter, setOrderIdFilter] = useState("");
//  const [userNameFilter, setUserNameFilter] = useState("");
//  const [statusFilter, setStatusFilter] = useState("");

//  useEffect(() => {
//   const fetchOrders = async () => {
//    try {
//     const response = await axios.get("https://app-api.carexpert.org.in/api/orders");
//     setOrders(response.data);
//    } catch (error) {
//     console.error("Error fetching orders:", error);
//    }
//   };

//   fetchOrders();
//  }, []);

//  const rows = orders.map((order, index) => ({
//   id: order._id || index, // ✅ safer unique id
//   order,
//   sNo: index + 1,
//  }));

//  const filteredRows = rows.filter((row) => {
//   const matchesOrderId = orderIdFilter
//    ? row.order.orderId?.toLowerCase().includes(orderIdFilter.toLowerCase())
//    : true;

//   const matchesMobile = userNameFilter
//    ? row.order.user?.mobile?.toString().includes(userNameFilter)
//    : true;

//   const matchesStatus = statusFilter
//    ? row.order.orderStatus?.toLowerCase().includes(statusFilter.toLowerCase())
//    : true;

//   return matchesOrderId && matchesMobile && matchesStatus;
//  });

//  const UserProfileCard = ({ order }) => {
//   const { user, car, address } = order;

//   return (
//    <Card
//     sx={{
//      display: "flex",
//      flexDirection: "row",
//      borderRadius: 3,
//      boxShadow: 3,
//      overflow: "hidden",
//      width: "100%",
//      height: "100%",
//     }}
//    >
//     {/* Left Side - Avatar & Car */}
//     <Box
//      sx={{
//       width: 150,
//       bgcolor: "#000D6B",
//       display: "flex",
//       flexDirection: "column",
//       alignItems: "center",
//       justifyContent: "center",
//       p: 1,
//       background: "#000D6B",
//      }}
//     >
//      <Avatar
//       src={car?.images}
//       sx={{
//        width: 80,
//        height: 80,
//        mb: 1,
//        bgcolor: "white",
//        img: {
//         objectFit: "contain",
//         width: "100%",
//         height: "100%",
//        },
//       }}
//      />

//      <Typography
//       variant="caption"
//       color="white"
//       sx={{ textAlign: "center", fontWeight: 500 }}
//      >
//       {car?.title}
//      </Typography>
//     </Box>

//     {/* Right Side - Info */}
//     <CardContent sx={{ flex: 1, px: 2 }}>
//      {/* User Info */}
//      {user && (
//       <>
//        <Typography variant="subtitle2" fontWeight={600}>
//         {user.name}
//        </Typography>
//        <Typography variant="body2" color="text.secondary">
//         Mobile: {user.mobile}
//        </Typography>
//       </>
//      )}

//      {/* Address Info */}
//      {address && (
//       <>
//        <Typography variant="body2">{address.houseFlatNumber}</Typography>
//        <Typography variant="body2" color="text.secondary">
//         {address.city}, {address.state} - {address.pincode}
//        </Typography>
//        <Typography variant="body2" color="text.secondary">
//         {address.completeAddress}
//        </Typography>
//        <Typography variant="body2" color="text.secondary">
//         Phone: {address.phoneNumber}
//        </Typography>
//       </>
//      )}
//     </CardContent>
//    </Card>
//   );
//  };

//  const columns = [
//   { field: "sNo", headerAlign: "center", headerName: "S.No", width: 50 },
//   {
//    field: "userAndAddressInfo",
//    headerAlign: "center",
//    headerName: "User, Car & Address Info",
//    width: 450,
//    renderCell: (params) => {
//     return <UserProfileCard order={params.row.order} />;
//    },
//   },

//   {
//    field: "orderAndCartInfo",
//    headerName: "Order & Cart Info",
//    width: 350,
//    headerAlign: "center",
//    renderCell: (params) => {
//     const order = params.row.order;
//     const cartItems = order.cart?.items || [];
//     const slot = order.slot || [];

//     return (
//      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
//       {/* Order Info */}
//       <Typography variant="subtitle2" fontWeight={600}>
//        Order Info
//       </Typography>
//       <Box sx={{ mb: 2 }}>
//        <Typography variant="body2">OrderID: {order.orderId}</Typography>
//        <Typography variant="body2">
//         Razorpay: {order.razorpayOrderId}
//        </Typography>
//        <Typography variant="body2">
//         Unique Order Id: {order.uniqueOrderId}
//        </Typography>
//        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
//         Slot Info
//        </Typography>

//        <Box sx={{ display: "flex", gap: 1.5 }}>
//         <Typography variant="body2">
//          <strong>Time:</strong> {slot?.time}
//         </Typography>
//         <Typography variant="body2">
//          <strong>Date:</strong> {new Date(slot?.date).toLocaleDateString()}
//         </Typography>
//        </Box>
//       </Box>

//       {/* Cart Info */}
//       <Typography variant="subtitle2" fontWeight={600}>
//        Cart Items
//       </Typography>
//       <Box>
//        {cartItems.length > 0 ? (
//         cartItems.map((item, idx) =>
//          item.product ? (
//           <Box
//            key={idx}
//            sx={{
//             display: "flex",
//             alignItems: "center",
//             gap: 1,
//             mb: 0.5,
//            }}
//           >
//            <Avatar src={item.product.image} sx={{ width: 30, height: 30 }} />
//            <Typography variant="body2">
//             {item.product.name} (₹{item.totalPrice})
//            </Typography>
//           </Box>
//          ) : (
//           <Typography key={idx} variant="body2" color="error">
//            Product removed or not available
//           </Typography>
//          )
//         )
//        ) : (
//         <Typography variant="body2">No Items</Typography>
//        )}
//       </Box>
//      </Box>
//     );
//    },
//   },
//   {
//    field: "cartAndStatusSummary",
//    headerName: "Cart & Status Info",
//    headerAlign: "center",
//    width: 500,
//    renderCell: (params) => {
//     const order = params.row.order;
//     const cart = order.cart;
//     const pricingDetails = order.pricingDetails;

//     // Filter out pricingDetails fields with value "0"
//     const filteredPricingDetails = Object.entries(pricingDetails || {}).filter(
//      ([_, value]) => value !== "0"
//     );

//     return (
//      <Box
//       sx={{
//        display: "flex",
//        flexDirection: "row",
//        gap: 2, // Space between the two sections
//        width: "100%",
//        p: 2,
//        bgcolor: "#f9f9f9",
//        borderRadius: 2,
//       }}
//      >
//       {/* Cart Summary */}
//       <Box
//        sx={{
//         display: "flex",
//         flexDirection: "column",
//         flex: 1, // Take up equal space
//         gap: 1,
//         bgcolor: "#f1f1f1",
//         p: 2,
//         borderRadius: 1,
//        }}
//       >
//        <Typography variant="subtitle2" sx={{ mb: 1 }}>
//         🛒 Cart Summary
//        </Typography>
//        {cart ? (
//         <>
//          <Typography variant="body2">Items: {cart.totalItems}</Typography>
//          <Typography variant="body2">Total: ₹{cart.totalPrice}</Typography>

//          {filteredPricingDetails.map(([key, value]) => (
//           <Typography key={key} variant="body2">
//            {key
//             .replace(/carexpert/g, "")
//             .replace(/([A-Z])/g, " $1")
//             .trim()}
//            : ₹{value}
//           </Typography>
//          ))}
//         </>
//        ) : (
//         <Typography variant="body2">No Cart Info</Typography>
//        )}
//       </Box>

//       {/* Order Status */}
//       <Box
//        sx={{
//         display: "flex",
//         flexDirection: "column",
//         flex: 1, // Take up equal space
//         gap: 1,
//         bgcolor: "#e9e9e9",
//         p: 2,
//         borderRadius: 1,
//        }}
//       >
//        <Typography variant="subtitle2" sx={{ mb: 1 }}>
//         📦 Order Status
//        </Typography>
//        <Typography variant="body2">
//         Order: {order.orderStatus || "N/A"}
//        </Typography>
//        <Typography variant="body2">
//         Payment: {order.paymentStatus || "N/A"}
//        </Typography>
//        <Typography variant="body2">
//         Placed on: {new Date(order.createdAt).toLocaleString()}
//        </Typography>
//       </Box>
//      </Box>
//     );
//    },
//   },
//  ];

//  return (
//   <Box sx={{ width: "100%", p: 2 }}>
//    {/* Filters */}
//    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
//     <TextField
//      label="Order ID"
//      size="small"
//      value={orderIdFilter}
//      onChange={(e) => setOrderIdFilter(e.target.value)}
//     />
//     <TextField
//      label="Mobile"
//      size="small"
//      value={userNameFilter}
//      onChange={(e) => setUserNameFilter(e.target.value)}
//     />
//     <TextField
//      label="Order Status"
//      size="small"
//      value={statusFilter}
//      onChange={(e) => setStatusFilter(e.target.value)}
//     />
//    </Box>

//    {/* DataGrid */}
//    <DataGrid
//     rows={filteredRows}
//     columns={columns}
//     pageSize={10}
//     rowsPerPageOptions={[5, 10, 20]}
//     pagination
//     disableSelectionOnClick
//     autoHeight
//     getRowHeight={() => "auto"} // Adjust row height for better visibility
//     components={{
//      Row: (props) => (
//       <>
//        <Divider sx={{ mb: 1 }} /> {/* Adding Divider Between Rows */}
//        <Box {...props} />
//       </>
//      ),
//     }}
//    />
//   </Box>
//  );
// };

// export default OrderManagement;
